import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 64. RATE LIMITING
// Protège : API · recherche · authentification · AI Tools · actions · webhooks.
// Les limites sont adaptées par : utilisateur · tenant · endpoint · outil · IP.
// On distingue les politiques (la règle) et les événements (les passages réels).
// ─────────────────────────────────────────────────────────────

export const RATE_LIMIT_SCOPES: { key: string; label: string; icon: string; desc: string }[] = [
  { key: 'api', label: 'API', icon: 'ri-code-s-slash-line', desc: 'Accès global à l’API interne et externe.' },
  { key: 'search', label: 'Recherche', icon: 'ri-search-line', desc: 'Moteur de recherche central.' },
  { key: 'auth', label: 'Authentification', icon: 'ri-login-box-line', desc: 'Connexion, inscription, réinitialisation.' },
  { key: 'ai_tools', label: 'AI Tools', icon: 'ri-robot-2-line', desc: 'Outils exposés à l’IA (coût et abus).' },
  { key: 'actions', label: 'Actions', icon: 'ri-flashlight-line', desc: 'Action Layer (créations métier).' },
  { key: 'webhooks', label: 'Webhooks', icon: 'ri-webhook-line', desc: 'Sorties webhook vers des tiers.' },
];

export function scopeMeta(key: string | null | undefined) {
  return RATE_LIMIT_SCOPES.find((s) => s.key === key) || RATE_LIMIT_SCOPES[0];
}

export const RATE_LIMIT_SUBJECTS: { key: string; label: string; icon: string; desc: string }[] = [
  { key: 'user', label: 'Utilisateur', icon: 'ri-user-line', desc: 'Limite par utilisateur.' },
  { key: 'tenant', label: 'Entreprise', icon: 'ri-building-2-line', desc: 'Limite par organisation (tenant).' },
  { key: 'endpoint', label: 'Endpoint', icon: 'ri-git-commit-line', desc: 'Limite par route / ressource.' },
  { key: 'tool', label: 'Outil', icon: 'ri-tools-line', desc: 'Limite par outil IA.' },
  { key: 'ip', label: 'Adresse IP', icon: 'ri-map-pin-2-line', desc: 'Limite par adresse IP (quand pertinent).' },
];

export function subjectMeta(key: string | null | undefined) {
  return RATE_LIMIT_SUBJECTS.find((s) => s.key === key) || RATE_LIMIT_SUBJECTS[1];
}

export interface CoreRateLimitPolicy {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  scope: string;
  subject_type: string;
  subject_key: string | null;
  label: string | null;
  limit_per_min: number;
  window_seconds: number;
  burst: number | null;
  is_default: boolean | null;
  enabled: boolean | null;
  metadata: Record<string, unknown> | null;
}

export interface RateLimitEvent {
  id: number;
  scope: string | null;
  subject_type: string | null;
  subject_key: string | null;
  endpoint: string | null;
  allowed: boolean | null;
  request_count: number | null;
  client_ip: string | null;
  created_at: string | null;
}

/** Politiques fusionnées : plateforme (défaut) écrasée par celles de l’entreprise. */
export async function listRateLimitPolicies(organizationId: number): Promise<CoreRateLimitPolicy[]> {
  const { data, error } = await supabase
    .from('core_rate_limit_policies')
    .select('*')
    .or(`organization_id.is.null,organization_id.eq.${organizationId}`)
    .order('scope', { ascending: true });
  if (error) throw error;
  const byKey = new Map<string, CoreRateLimitPolicy>();
  (data || []).forEach((row) => {
    const p = row as CoreRateLimitPolicy;
    const key = `${p.scope}:${p.subject_type}`;
    const existing = byKey.get(key);
    if (!existing || p.organization_id != null) byKey.set(key, p);
  });
  return Array.from(byKey.values());
}

export async function saveRateLimitPolicy(input: {
  tenantId?: number | null;
  organizationId: number;
  scope: string;
  subjectType: string;
  limitPerMin: number;
  windowSeconds?: number;
  burst?: number;
  label?: string | null;
  enabled?: boolean;
}): Promise<void> {
  const existing = await supabase
    .from('core_rate_limit_policies')
    .select('id')
    .eq('organization_id', input.organizationId)
    .eq('scope', input.scope)
    .eq('subject_type', input.subjectType)
    .maybeSingle();
  const payload = {
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId,
    scope: input.scope,
    subject_type: input.subjectType,
    label: input.label ?? `${scopeMeta(input.scope).label} · ${subjectMeta(input.subjectType).label}`,
    limit_per_min: input.limitPerMin,
    window_seconds: input.windowSeconds ?? 60,
    burst: input.burst ?? 0,
    enabled: input.enabled ?? true,
    is_default: false,
    updated_at: new Date().toISOString(),
  };
  if (existing.data?.id) {
    const { error } = await supabase.from('core_rate_limit_policies').update(payload).eq('id', existing.data.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('core_rate_limit_policies').insert(payload);
    if (error) throw error;
  }
}

export async function toggleRateLimitPolicy(id: number, enabled: boolean): Promise<void> {
  const { error } = await supabase
    .from('core_rate_limit_policies')
    .update({ enabled, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteRateLimitPolicy(id: number): Promise<void> {
  const { error } = await supabase.from('core_rate_limit_policies').delete().eq('id', id);
  if (error) throw error;
}

export async function listRateLimitEvents(organizationId: number, limit = 60): Promise<RateLimitEvent[]> {
  const { data, error } = await supabase
    .from('core_rate_limit_events')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as RateLimitEvent[];
}

export async function logRateLimitEvent(input: {
  tenantId?: number | null;
  organizationId: number;
  scope: string;
  subjectType: string;
  subjectKey?: string | null;
  policyId?: number | null;
  endpoint?: string | null;
  allowed: boolean;
  clientIp?: string | null;
}): Promise<void> {
  await supabase.from('core_rate_limit_events').insert({
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId,
    scope: input.scope,
    subject_type: input.subjectType,
    subject_key: input.subjectKey ?? null,
    policy_id: input.policyId ?? null,
    endpoint: input.endpoint ?? null,
    allowed: input.allowed,
    window_start: new Date().toISOString(),
    client_ip: input.clientIp ?? null,
  });
}

export interface RateLimitStats {
  total: number;
  enabled: number;
  scopes: number;
  blocked24h: number;
}

export async function fetchRateLimitStats(organizationId: number): Promise<RateLimitStats> {
  const policies = await listRateLimitPolicies(organizationId);
  const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
  const { count } = await supabase
    .from('core_rate_limit_events')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .eq('allowed', false)
    .gte('created_at', since);
  return {
    total: policies.length,
    enabled: policies.filter((p) => p.enabled).length,
    scopes: new Set(policies.map((p) => p.scope)).size,
    blocked24h: count || 0,
  };
}

/** Estime l’état d’une limite à partir de l’usage courant (fenêtre glissante). */
export function simulateLimitCheck(policy: CoreRateLimitPolicy, usedInWindow: number) {
  const limit = policy.limit_per_min ?? 60;
  const windowSeconds = policy.window_seconds ?? 60;
  const allowed = usedInWindow < limit;
  return {
    allowed,
    limit,
    used: usedInWindow,
    remaining: Math.max(0, limit - usedInWindow),
    windowSeconds,
    utilization: limit > 0 ? Math.min(100, Math.round((usedInWindow / limit) * 100)) : 0,
  };
}

// ─────────────────────────────────────────────────────────────
// Enforcement côté APPLICATION (spec 64)
// Le rate limiting tournait jusqu’ici côté Edge Function (clé d’API). On
// l’étend aux APPELS AUTHENTIFIÉS PAR UTILISATEUR : la recherche, la
// connexion et les AI Tools vérifient désormais la limite AVANT exécution,
// en s’appuyant sur le journal des passages (fenêtre glissante) et en
// journalisant chaque décision. Aucune donnée n’est exposée : tout est scopé
// par organisation et par sujet (utilisateur / outil / IP).
// ─────────────────────────────────────────────────────────────

/** Surfaces réellement appliquées côté application (en plus de l’Edge Function). */
export const APP_ENFORCED_SCOPES: { scope: string; label: string; subject: string; icon: string }[] = [
  { scope: 'search', label: 'Recherche globale', subject: 'Par utilisateur', icon: 'ri-search-line' },
  { scope: 'auth', label: 'Connexion', subject: 'Par identifiant / IP', icon: 'ri-login-box-line' },
  { scope: 'ai_tools', label: 'AI Tools', subject: 'Par outil', icon: 'ri-robot-2-line' },
  { scope: 'actions', label: 'Actions', subject: 'Par utilisateur', icon: 'ri-flashlight-line' },
];

export interface RateLimitDecision {
  allowed: boolean;
  scope: string;
  subjectType: string;
  subjectKey: string;
  limit: number;
  used: number;
  remaining: number;
  windowSeconds: number;
  retryAfterSeconds: number;
  policyId: number | null;
  reason?: string;
}

/** Politique applicable : correspondance exacte du sujet, sinon 1ʳᵉ active du scope. */
export function resolvePolicyForScope(
  policies: CoreRateLimitPolicy[],
  scope: string,
  subjectType?: string,
): CoreRateLimitPolicy | null {
  const enabled = policies.filter((p) => p.scope === scope && p.enabled !== false);
  if (enabled.length === 0) return null;
  if (subjectType) {
    const exact = enabled.find((p) => p.subject_type === subjectType);
    if (exact) return exact;
  }
  return enabled[0];
}

/** Compte les passages récents (fenêtre glissante) pour un scope + sujet. */
export async function countRecentRateLimitEvents(input: {
  organizationId: number | null;
  scope: string;
  subjectKey: string;
  windowSeconds: number;
}): Promise<number> {
  const since = new Date(Date.now() - input.windowSeconds * 1000).toISOString();
  let query = supabase
    .from('core_rate_limit_events')
    .select('id', { count: 'exact', head: true })
    .eq('scope', input.scope)
    .eq('subject_key', input.subjectKey)
    .gte('created_at', since);
  if (input.organizationId != null) query = query.eq('organization_id', input.organizationId);
  else query = query.is('organization_id', null);
  const { count } = await query;
  return count || 0;
}

/**
 * Vérifie une limite AVANT exécution d’une opération applicative, puis
 * journalise la décision. Best-effort : si la vérification échoue, on
 * AUTORISE (on ne bloque jamais un utilisateur à cause d’un cache hs).
 */
export async function checkRateLimit(input: {
  organizationId: number | null;
  tenantId?: number | null;
  scope: string;
  subjectType: string;
  subjectKey: string;
  endpoint?: string | null;
  clientIp?: string | null;
}): Promise<RateLimitDecision> {
  const fallback: RateLimitDecision = {
    allowed: true, scope: input.scope, subjectType: input.subjectType, subjectKey: input.subjectKey,
    limit: 0, used: 0, remaining: 0, windowSeconds: 60, retryAfterSeconds: 0, policyId: null,
    reason: 'vérification indisponible',
  };
  try {
    let policies: CoreRateLimitPolicy[] = [];
    if (input.organizationId != null) {
      policies = await listRateLimitPolicies(input.organizationId);
    } else {
      const { data } = await supabase
        .from('core_rate_limit_policies')
        .select('*')
        .is('organization_id', null)
        .eq('enabled', true);
      policies = (data || []) as CoreRateLimitPolicy[];
    }

    const policy = resolvePolicyForScope(policies, input.scope, input.subjectType);
    if (!policy) return { ...fallback, reason: 'aucune politique active' };

    const windowSeconds = policy.window_seconds ?? 60;
    const limit = policy.limit_per_min ?? 60;
    const used = await countRecentRateLimitEvents({
      organizationId: input.organizationId,
      scope: input.scope,
      subjectKey: input.subjectKey,
      windowSeconds,
    });
    const allowed = used < limit;

    // Journalise la décision (fenêtre glissante).
    try {
      await logRateLimitEvent({
        tenantId: input.tenantId ?? null,
        organizationId: input.organizationId as number,
        scope: input.scope,
        subjectType: policy.subject_type,
        subjectKey: input.subjectKey,
        policyId: policy.id,
        endpoint: input.endpoint ?? null,
        allowed,
        clientIp: input.clientIp ?? null,
      });
    } catch {
      /* le journal ne doit pas bloquer la décision */
    }

    return {
      allowed,
      scope: input.scope,
      subjectType: policy.subject_type,
      subjectKey: input.subjectKey,
      limit,
      used,
      remaining: Math.max(0, limit - used - 1),
      windowSeconds,
      retryAfterSeconds: allowed ? 0 : windowSeconds,
      policyId: policy.id,
    };
  } catch {
    return fallback;
  }
}

/** Message utilisateur prêt à l’emploi pour un refus de limite. */
export function rateLimitMessage(d: RateLimitDecision): string {
  const label = scopeMeta(d.scope).label;
  return `Trop de requêtes (${label} : ${d.limit}/${d.windowSeconds}s). Réessayez dans ${d.retryAfterSeconds}s.`;
}